import { DAY_IN_MS } from "./websitePromotionDelivery";

export const ROTATION_LOCAL_STORE_KEY = "ecotour:portal:rotation:local";
export const ROTATION_SESSION_STORE_KEY = "ecotour:portal:rotation:session";
export const DEFAULT_ROTATION_WINDOW_DAYS = 14;

export const EMPTY_ROTATION_STORE = {
  exposures: {},
};

const normalizeObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const safeParseJSON = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const normalizeText = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .slice(0, 200);

const normalizeItems = (items = []) => (Array.isArray(items) ? items.filter(Boolean) : []);

const hashString = (value = "") => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0);
};

export const buildRotationSurfaceKey = (...parts) =>
  parts
    .map((part) => normalizeText(part))
    .filter(Boolean)
    .join(":");

export const getRotationItemKey = (item = {}, fallbackIndex = 0) => {
  const id = String(item?.id || item?._id || item?.slug || item?.key || `item-${fallbackIndex}`).trim();
  const version = String(item?.updatedAt || item?.lastUpdated || "").trim();
  return version ? `${id}:${version}` : id;
};

export const normalizeExposureTimestamps = (
  value,
  { windowMs = null, limit = 80, now = Date.now() } = {},
) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => Number(entry))
    .filter((entry) => Number.isFinite(entry) && entry > 0)
    .filter((entry) => (windowMs ? entry >= now - windowMs : true))
    .slice(-limit);

const resolveStorageKey = (storageType) =>
  storageType === "session" ? ROTATION_SESSION_STORE_KEY : ROTATION_LOCAL_STORE_KEY;

export const readRotationStore = (storageType) => {
  if (typeof window === "undefined") return EMPTY_ROTATION_STORE;
  const storage = storageType === "session" ? window.sessionStorage : window.localStorage;
  const storageKey = resolveStorageKey(storageType);

  try {
    const parsed = safeParseJSON(storage.getItem(storageKey));
    return {
      exposures: normalizeObject(parsed?.exposures),
    };
  } catch {
    return EMPTY_ROTATION_STORE;
  }
};

export const writeRotationStore = (storageType, nextStore) => {
  if (typeof window === "undefined") return;
  const storage = storageType === "session" ? window.sessionStorage : window.localStorage;
  const storageKey = resolveStorageKey(storageType);
  try {
    storage.setItem(
      storageKey,
      JSON.stringify({
        exposures: normalizeObject(nextStore?.exposures),
      }),
    );
  } catch {
    // ignore storage failures
  }
};

const buildExposureKey = ({
  surfaceKey = "",
  actorKey = "",
  itemKey = "",
}) => `${surfaceKey || "surface"}:${actorKey || "visitor:anonymous"}:${itemKey}`;

const resolveRotationWindowMs = (windowDays = DEFAULT_ROTATION_WINDOW_DAYS) =>
  Math.max(Number(windowDays || DEFAULT_ROTATION_WINDOW_DAYS), 1) * DAY_IN_MS;

export const orderRotatableItemsForVisitor = (
  items = [],
  {
    surfaceKey = "",
    actorKey = "",
    localStore = EMPTY_ROTATION_STORE,
    sessionStore = EMPTY_ROTATION_STORE,
    windowDays = DEFAULT_ROTATION_WINDOW_DAYS,
    now = Date.now(),
  } = {},
) => {
  const list = normalizeItems(items);
  if (list.length <= 1) return list;

  const normalizedSurfaceKey = buildRotationSurfaceKey(surfaceKey);
  if (!normalizedSurfaceKey) return list;

  const windowMs = resolveRotationWindowMs(windowDays);

  const ranked = list
    .map((item, index) => {
      const itemKey = getRotationItemKey(item, index);
      const exposureKey = buildExposureKey({
        surfaceKey: normalizedSurfaceKey,
        actorKey,
        itemKey,
      });
      const sessionHistory = normalizeExposureTimestamps(sessionStore?.exposures?.[exposureKey], {
        now,
        limit: 40,
      });
      const localHistory = normalizeExposureTimestamps(localStore?.exposures?.[exposureKey], {
        now,
        windowMs,
        limit: 80,
      });
      const sessionLastSeenAt = sessionHistory[sessionHistory.length - 1] || 0;
      const localLastSeenAt = localHistory[localHistory.length - 1] || 0;

      return {
        item,
        index,
        itemKey,
        sessionCount: sessionHistory.length,
        windowCount: localHistory.length,
        sessionLastSeenAt,
        lastSeenAt: Math.max(sessionLastSeenAt, localLastSeenAt),
        stableRank: hashString(`${actorKey || "visitor"}:${normalizedSurfaceKey}:${itemKey}`),
      };
    })
    .sort((left, right) => {
      if ((left.sessionCount === 0) !== (right.sessionCount === 0)) {
        return left.sessionCount === 0 ? -1 : 1;
      }
      if ((left.windowCount === 0) !== (right.windowCount === 0)) {
        return left.windowCount === 0 ? -1 : 1;
      }
      if (left.windowCount !== right.windowCount) {
        return left.windowCount - right.windowCount;
      }
      if (left.lastSeenAt !== right.lastSeenAt) {
        return left.lastSeenAt - right.lastSeenAt;
      }
      if (left.stableRank !== right.stableRank) {
        return left.stableRank - right.stableRank;
      }
      return left.index - right.index;
    });

  const mostRecentSessionItem = ranked.reduce((current, candidate) => {
    if (!candidate.sessionLastSeenAt) return current;
    if (!current || candidate.sessionLastSeenAt > current.sessionLastSeenAt) {
      return candidate;
    }
    return current;
  }, null);

  if (
    mostRecentSessionItem &&
    ranked.length > 1 &&
    ranked[0]?.itemKey === mostRecentSessionItem.itemKey
  ) {
    const [first, ...rest] = ranked;
    return [...rest, first].map((entry) => entry.item);
  }

  return ranked.map((entry) => entry.item);
};

export const recordRotatableItemExposure = (
  item = {},
  {
    surfaceKey = "",
    actorKey = "",
    localStore = EMPTY_ROTATION_STORE,
    sessionStore = EMPTY_ROTATION_STORE,
    windowDays = DEFAULT_ROTATION_WINDOW_DAYS,
    now = Date.now(),
  } = {},
) => {
  const normalizedSurfaceKey = buildRotationSurfaceKey(surfaceKey);
  const itemKey = getRotationItemKey(item);

  if (!normalizedSurfaceKey || !itemKey) {
    return {
      local: {
        exposures: normalizeObject(localStore?.exposures),
      },
      session: {
        exposures: normalizeObject(sessionStore?.exposures),
      },
    };
  }

  const exposureKey = buildExposureKey({
    surfaceKey: normalizedSurfaceKey,
    actorKey,
    itemKey,
  });
  const windowMs = resolveRotationWindowMs(windowDays);
  const nextLocalTimestamps = [
    ...normalizeExposureTimestamps(localStore?.exposures?.[exposureKey], {
      now,
      windowMs,
      limit: 80,
    }),
    now,
  ].slice(-80);
  const nextSessionTimestamps = [
    ...normalizeExposureTimestamps(sessionStore?.exposures?.[exposureKey], {
      now,
      limit: 40,
    }),
    now,
  ].slice(-40);

  return {
    local: {
      exposures: {
        ...normalizeObject(localStore?.exposures),
        [exposureKey]: nextLocalTimestamps,
      },
    },
    session: {
      exposures: {
        ...normalizeObject(sessionStore?.exposures),
        [exposureKey]: nextSessionTimestamps,
      },
    },
  };
};

